/**
 * Cubic-bezier easing function factory
 * Creates an easing function based on cubic-bezier control points
 * https://developer.mozilla.org/en-US/blog/custom-easing-in-css-with-linear/
 * 
 * @param x1 - First control point x coordinate
 * @param y1 - First control point y coordinate
 * @param x2 - Second control point x coordinate
 * @param y2 - Second control point y coordinate
 * @returns A function that takes progress (0-1, time fraction) and returns eased progress (0-1, output value)
 * 
 * Correct implementation: For input progress (time fraction), binary search for t where x(t) ≈ progress,
 * then return y(t). This ensures proper easing along the curve.
 */
export function createCubicBezierEasing(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): (progress: number) => number {
  // Calculate the x value for a given t (time parameter)
  const getX = (t: number): number => {
    const mt = 1 - t;
    return 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t;
  };

  // Calculate the y value for a given t (time parameter)
  const getY = (t: number): number => {
    const mt = 1 - t;
    return 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t;
  };

  // Binary search to find t where x(t) ≈ progress, then return y(t)
  return (progress: number): number => {
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;

    let t0 = 0;
    let t1 = 1;
    let t = 0.5;

    // Binary search with precision - find t where x(t) matches the input progress
    for (let i = 0; i < 12; i++) {
      const x = getX(t);
      if (Math.abs(x - progress) < 0.0001) break;
      if (x < progress) {
        t0 = t;
      } else {
        t1 = t;
      }
      t = (t0 + t1) / 2;
    }

    // Return the y-value at the found t, which is the eased output
    return getY(t);
  };
}
